import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Button,
} from "@mui/material";
import { X, CloudUpload, Trash2, FileAudio } from "lucide-react";
import { TriangleAlert } from "lucide-react";

export default function ImportFileModal({ open, onClose, onGenerate, onFileUpload, onRemoveFile, files = [], isUploading = false }) {
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            // Only take the first file for single file upload
            onFileUpload([droppedFiles[0]]);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length > 0) {
            // Only take the first file for single file upload
            onFileUpload([selectedFiles[0]]);
        }
        // Reset input to allow selecting same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatSize = (bytes) => {
        if (bytes === 0 || !bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleClose = () => {
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                style: { borderRadius: 16, padding: "20px", maxWidth: "600px" },
            }}
        >
            <div className="flex justify-between items-center mb-6">
                <Typography variant="h6" style={{ fontWeight: 500, fontSize: "20px" }}>
                    Import File
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <X className="w-5 h-5 text-gray-500" />
                </IconButton>
            </div>
          

            <DialogContent className="p-0 overflow-visible flex flex-col gap-2">
                {/* Drag and Drop Area */}
                <div
                    className={`border-2 border-dashed rounded-[12px] p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? "border-[#D0D5DD] bg-[#1B5C290D]" : "border-[#D0D5DD] hover:border-[#D0D5DD]"
                        }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".mp3,.wav,.mp4,.m4a"
                        onChange={handleFileSelect}
                    />
                    <CloudUpload className="w-10 h-10 text-[#1B5C29] mb-3" strokeWidth={1.5} />
                    <Typography variant="body1" className="text-center mb-1">
                        <span className="text-[#1B5C29] font-semibold underline">Choose to upload</span> or drag and drop it here
                    </Typography>
                    <Typography variant="body2" className="text-[#000000BF] text-xs">
                        Up to 2GB in size. Supports: mp3,mp4,wav
                    </Typography>

                </div>

                {/* Uploading indicator */}
                {isUploading && (
                    <div className="flex items-center gap-3 py-2">
                        <svg
                            className="animate-spin h-6 w-6 text-[#1B5C29] shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-base text-gray-500 font-medium">Uploading File...</span>
                    </div>
                )}

                {/* Completed file list */}
                {files.some(f => f.status === 'completed') && (
                    <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto">
                        {files.map((file, index) =>
                            file.status === 'completed' ? (
                                <div key={`${file.fileName}-${index}`} className="border border-gray-100 rounded-lg p-3 flex items-center justify-between shadow-sm">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="p-2 rounded bg-orange-50 shrink-0">
                                            <FileAudio className="w-6 h-6 text-orange-500" />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <Typography variant="body2" className="font-medium truncate text-gray-900 w-[70%]">
                                                    {file.fileName}
                                                </Typography>
                                                <button onClick={() => onRemoveFile(index)} className="text-gray-400 hover:text-red-500">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs text-gray-500">{formatSize(file.size)}</span>
                                                <span className="text-xs text-gray-500 shrink-0">
                                                    Completed
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>
                )}

                  <div className="">
                <Typography variant="body2" className="text-[#F4711F] text-xs mt-1 font-medium flex items-center gap-2">
                     <TriangleAlert className="h-4 w-4" /> Note : File duration must not exceed 300 minutes
                </Typography>
            </div>

                {/* Generate Button */}
                <div className="flex justify-center mt-2">
                    <Button
                        variant="contained"
                        onClick={onGenerate}
                        disabled={files.length === 0 || isUploading}
                        style={{
                            backgroundColor: "#1B5C29",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "10px 32px",
                            fontSize: "16px",
                            height: "40px",
                            fontWeight: 500,
                            boxShadow: "none",
                            color: "white"
                        }}
                    >
                        Generate
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


ImportFileModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    onFileUpload: PropTypes.func.isRequired,
    onRemoveFile: PropTypes.func.isRequired,
    files: PropTypes.array,
    isUploading: PropTypes.bool
};
